import { escapeHtml } from "./pdfUtils";

const DEFAULT_THEME = {
  primary: "#7d6bff",
  secondary: "#5ef0d8",
  tertiary: "#f7c35f",
};

function sanitizeColor(value, fallback) {
  const raw = String(value || "").trim();
  if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(raw)) return raw;
  return fallback;
}

export function buildCoupleMatchPdfHtml({
  exportedAt,
  partnerA = {},
  partnerB = {},
  result,
  labels = {},
  theme = {},
  t,
  narratives = null,
}) {
  const l = labels;
  const themePrimary = sanitizeColor(theme.primary, DEFAULT_THEME.primary);
  const themeSecondary = sanitizeColor(theme.secondary, DEFAULT_THEME.secondary);
  const themeTertiary = sanitizeColor(theme.tertiary, DEFAULT_THEME.tertiary);
  const n = narratives || result?.narratives || null;
  const life = n?.lifeTogether || result?.life_together || {};

  const partnerLine = (partner, fallbackLabel) => {
    const name = partner?.name?.trim();
    const dob = partner?.birthDate?.trim();
    const parts = [name || fallbackLabel, dob].filter(Boolean);
    return parts.join(" · ");
  };

  const dimensionsHtml = (result.dimensions || [])
    .map(
      (dim) => `
      <div class="row">
        <span>${escapeHtml(t(`couple_match_dim_${dim.key}`))}</span>
        <span class="score-pill">${dim.score}%</span>
      </div>
    `
    )
    .join("");

  const listHtml = (items) =>
    (items || [])
      .filter(Boolean)
      .map((item) => `<li>${escapeHtml(item)}</li>`)
      .join("");

  const topYears = (n?.jointYears || result?.joint_timeline || [])
    .filter((y) => y.tone === "auspicious")
    .slice(0, 3)
    .map((y) => `<div class="year-line">${escapeHtml(`${y.year}: ${y.narrative?.text || y.tone}`)}</div>`)
    .join("");

  return `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400&display=swap" rel="stylesheet">
    <style>
      :root {
        --accent-primary: ${themePrimary};
        --accent-secondary: ${themeSecondary};
        --accent-tertiary: ${themeTertiary};
      }
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: "DM Sans", "Inter", "Segoe UI", Arial, sans-serif;
        color: #e8ebff;
        background: #050816;
      }
      .sheet {
        border-radius: 18px;
        border: 1px solid var(--accent-secondary);
        background: #090d23;
        overflow: hidden;
      }
      .header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: flex-end;
        padding: 24px 28px 18px;
        border-bottom: 1px solid color-mix(in srgb, var(--accent-primary) 28%, transparent);
      }
      .brand {
        font-family: "Playfair Display", serif;
        font-size: 22px;
        color: var(--accent-secondary);
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      .eyebrow {
        font-size: 10px;
        color: #99a3c4;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        margin-top: 4px;
      }
      .title {
        margin-top: 10px;
        font-family: "Playfair Display", serif;
        font-size: 28px;
        color: #f6f7ff;
      }
      .date-meta {
        text-align: right;
        font-family: "DM Mono", monospace;
        font-size: 10px;
        line-height: 1.7;
        color: #95a0bf;
      }
      .date-meta strong {
        display: block;
        font-size: 12px;
        color: var(--accent-primary);
      }
      .content { padding: 22px 24px 20px; }
      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        margin-bottom: 14px;
      }
      .info-card {
        border: 1px solid rgba(255,255,255,0.14);
        border-radius: 12px;
        background: rgba(255,255,255,0.03);
        padding: 10px 12px;
      }
      .label {
        font-size: 10px;
        letter-spacing: 0.15em;
        color: #95a0bf;
        text-transform: uppercase;
        margin-bottom: 6px;
      }
      .value { font-size: 13px; color: #f1f3ff; }
      .panel {
        border-radius: 14px;
        border: 1px solid rgba(255,255,255,0.12);
        background: rgba(255,255,255,0.03);
        padding: 14px;
        margin-bottom: 12px;
      }
      .panel-title {
        font-family: "Playfair Display", serif;
        color: #f6f7ff;
        font-size: 19px;
        margin: 0 0 10px;
      }
      .score {
        font-family: "Playfair Display", serif;
        font-size: 42px;
        color: var(--accent-primary);
        line-height: 1;
      }
      .band {
        margin-top: 6px;
        font-size: 12px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--accent-secondary);
      }
      .row {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        margin: 6px 0;
        font-size: 13px;
        color: #d8dcf5;
      }
      .score-pill { color: var(--accent-primary); }
      p, .year-line {
        line-height: 1.7;
        font-size: 14px;
        color: #c8c4e8;
        margin: 0 0 8px;
      }
      ul {
        color: #c8c4e8;
        font-size: 13px;
        line-height: 1.6;
        margin: 0;
        padding-left: 18px;
      }
      .footer {
        margin-top: 4px;
        padding: 0 24px 14px;
        font-size: 10px;
        color: #8f9abb;
        font-family: "DM Mono", monospace;
        letter-spacing: 0.12em;
        display: flex;
        justify-content: space-between;
      }
      @media print {
        html, body { height: 100%; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
    <section class="sheet">
      <header class="header">
        <div>
          <div class="brand">Ecliptica</div>
          <div class="eyebrow">${escapeHtml(l.eyebrow || "")}</div>
          <div class="title">${escapeHtml(l.title || "Couple Match")}</div>
        </div>
        <div class="date-meta">
          <strong>${escapeHtml(l.exported || "Exported")}</strong>
          ${escapeHtml(exportedAt || "")}
        </div>
      </header>
      <div class="content">
        <div class="info-grid">
          <div class="info-card">
            <div class="label">${escapeHtml(l.partnerA || "Partner A")}</div>
            <div class="value">${escapeHtml(partnerLine(partnerA, l.partnerA))}</div>
          </div>
          <div class="info-card">
            <div class="label">${escapeHtml(l.partnerB || "Partner B")}</div>
            <div class="value">${escapeHtml(partnerLine(partnerB, l.partnerB))}</div>
          </div>
        </div>
        <div class="panel">
          <div class="panel-title">${escapeHtml(l.overall || "Overall")}</div>
          <div class="score">${result.overall_score}/100</div>
          <div class="band">${escapeHtml(t(`couple_match_band_${result.overall_band}`))}</div>
        </div>
        <div class="panel">
          <div class="panel-title">${escapeHtml(l.dimensions || "Dimensions")}</div>
          ${dimensionsHtml}
        </div>
        ${n?.matrix?.summary
          ? `<div class="panel"><div class="panel-title">${escapeHtml(l.matrixSection || "Matrix")}</div><p>${escapeHtml(n.matrix.summary)}</p></div>`
          : ""}
        ${n?.elements?.profiles?.length
          ? `<div class="panel"><div class="panel-title">${escapeHtml(l.elementsSection || "Elements")}</div>${n.elements.profiles
              .map(
                (p) =>
                  `<p><strong>${escapeHtml(p.element)} · ${escapeHtml(p.tierLabel)} (${p.count.toFixed(1)})</strong><br/>${escapeHtml(p.text)}</p>`
              )
              .join("")}</div>`
          : ""}
        ${topYears
          ? `<div class="panel"><div class="panel-title">${escapeHtml(l.topYearsSection || "Top auspicious years")}</div>${topYears}</div>`
          : ""}
        <div class="panel">
          <div class="panel-title">${escapeHtml(l.lifeTogether || "Life together")}</div>
          ${life.headline ? `<p><strong>${escapeHtml(life.headline)}</strong></p>` : ""}
          ${life.text ? `<p>${escapeHtml(life.text)}</p>` : ""}
          ${life.strengths?.length ? `<p><strong>${escapeHtml(l.strengths || t("couple_match_life_strengths_title"))}</strong></p><ul>${listHtml(life.strengths)}</ul>` : ""}
          ${life.watchouts?.length ? `<p><strong>${escapeHtml(l.watchouts || t("couple_match_life_watchouts_title"))}</strong></p><ul>${listHtml(life.watchouts)}</ul>` : ""}
          ${life.plan?.length ? `<p><strong>${escapeHtml(l.plan || t("couple_match_life_plan_title"))}</strong></p><ul>${listHtml(life.plan)}</ul>` : ""}
        </div>
      </div>
      <footer class="footer">
        <span>${escapeHtml(l.report || "")}</span>
        <span>${escapeHtml(l.generatedBy || "")}</span>
      </footer>
    </section>
  `;
}
